import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Cloud, CloudOff, FolderPlus, RefreshCw, Loader2, CheckCircle2, AlertCircle, X } from "lucide-react";
import { toast } from "sonner";

export default function NextcloudBlock({ cid }) {
  const [status, setStatus] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/clienti/${cid}/nextcloud/status`);
      setStatus(data);
    } catch { setStatus(null); }
  };

  const fetchJob = async () => {
    try {
      const { data } = await api.get(`/clienti/${cid}/nextcloud/job`);
      if (data && data.id) {
        setJob(data);
        if (data.status === "done" || data.status === "failed") {
          stopPolling();
          if (data.status === "done") {
            const r = data.result || {};
            if (data.kind === "cliente_folders") {
              toast.success(`Create ${r.count || 0} cartelle su Aruba Drive`);
            } else if (data.kind === "cliente_sync") {
              if ((r.failed || 0) > 0) toast.warning(`Sincronizzati ${r.synced} file (${r.failed} falliti)`);
              else if ((r.synced || 0) === 0) toast.info("Tutto già sincronizzato");
              else toast.success(`${r.synced} file sincronizzati`);
            }
          } else {
            toast.error(`Aruba Drive: ${data.error || "errore sconosciuto"}`);
          }
          await load();
        }
      } else {
        setJob(null);
      }
    } catch {}
  };

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(fetchJob, 1500);
  };
  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    load();
    fetchJob().then(() => {
      // se c'è un job ancora in esecuzione, riprendi il polling
    });
    return () => stopPolling();
    // eslint-disable-next-line
  }, [cid]);

  // Continue polling if job is still queued/running on first load
  useEffect(() => {
    if (job && (job.status === "queued" || job.status === "running")) startPolling();
    else stopPolling();
    // eslint-disable-next-line
  }, [job?.status]);

  const isWorking = job && (job.status === "queued" || job.status === "running");
  const isFolders = job?.kind === "cliente_folders" && isWorking;
  const isSync = job?.kind === "cliente_sync" && isWorking;

  const createFolders = async () => {
    try {
      const { data } = await api.post(`/clienti/${cid}/nextcloud/folders`);
      toast.info(data.message || "Avviata creazione cartelle");
      setJob({ id: data.job_id, kind: "cliente_folders", status: "queued", progress: 0, total: 0 });
      window.dispatchEvent(new Event("nurel:job-fired"));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const syncFiles = async () => {
    try {
      const { data } = await api.post(`/clienti/${cid}/nextcloud/sync`);
      toast.info(data.message || "Sincronizzazione avviata");
      setJob({ id: data.job_id, kind: "cliente_sync", status: "queued", progress: 0, total: 0 });
      window.dispatchEvent(new Event("nurel:job-fired"));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const dismissJob = () => setJob(null);

  if (!status) return null;

  if (!status.configured) {
    return (
      <div className="card-shell p-4 flex items-center gap-3 text-sm" data-testid="nc-not-configured">
        <CloudOff className="w-5 h-5" style={{ color: "var(--text-muted)" }} />
        <div className="flex-1" style={{ color: "var(--text-muted)" }}>
          Aruba Drive non configurato (né per questa commessa né globalmente).
        </div>
        <a href="/impostazioni" className="btn-secondary text-xs py-1 px-2">Configura</a>
      </div>
    );
  }

  return (
    <div className="card-shell p-5" data-testid="nextcloud-block">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#E8F3EB", color: "var(--primary)" }}>
          <Cloud className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold" style={{ fontFamily: "Manrope" }}>Aruba Drive</div>
          <div className="text-xs flex flex-wrap items-center gap-x-2" style={{ color: "var(--text-muted)" }}>
            <span>Credenziali: <strong>{status.source_creds}</strong></span>
            <span>•</span>
            <span>Cartella: <strong>{status.source_folder}</strong></span>
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            {status.folders_created ? (
              <><CheckCircle2 className="inline w-3.5 h-3.5 mr-1" style={{ color: "var(--primary)" }} /><code className="text-xs">{status.cliente_root}</code></>
            ) : (
              <><AlertCircle className="inline w-3.5 h-3.5 mr-1" style={{ color: "#8A4D0F" }} />Cartelle cliente non ancora create · Destinazione: <code>{status.cliente_root}</code></>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase font-semibold tracking-widest" style={{ color: "var(--text-muted)" }}>Sync</div>
          <div className="font-bold">
            <span style={{ color: "var(--primary)" }}>{status.synced_files}</span>
            <span style={{ color: "var(--text-muted)" }}> / {status.total_files}</span>
          </div>
        </div>
      </div>

      {/* Background job progress banner */}
      {job && (
        <div className="mb-3 p-3 rounded-md flex items-center gap-3" style={{ background: job.status === "failed" ? "#FDECEA" : isWorking ? "#FFF8E1" : "#E6F4EA", color: job.status === "failed" ? "#B30000" : isWorking ? "#7A4500" : "#1B5E20" }} data-testid="nc-job-banner">
          {isWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : job.status === "done" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          <div className="flex-1 text-xs">
            <div className="font-semibold">
              {job.kind === "cliente_folders" && "Creazione cartelle"}
              {job.kind === "cliente_sync" && "Sincronizzazione file"}
              {" · "}
              {job.status === "queued" && "in coda…"}
              {job.status === "running" && (job.total > 0 ? `${job.progress}/${job.total}` : "in corso…")}
              {job.status === "done" && "completata"}
              {job.status === "failed" && "fallita"}
            </div>
            {job.status === "failed" && job.error && <div className="opacity-80 mt-0.5">{job.error}</div>}
            {isWorking && job.total > 0 && (
              <div className="h-1 rounded-full mt-1.5 overflow-hidden" style={{ background: "rgba(0,0,0,0.08)" }}>
                <div className="h-full transition-all" style={{ width: `${Math.min(100, (job.progress / job.total) * 100)}%`, background: "currentColor" }} />
              </div>
            )}
          </div>
          {!isWorking && (
            <button onClick={dismissJob} className="p-1 rounded hover:bg-black/5" data-testid="nc-job-dismiss"><X className="w-3.5 h-3.5" /></button>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button onClick={createFolders} disabled={isFolders} className="btn-secondary text-xs disabled:opacity-50" data-testid="nc-create-folders">
          {isFolders ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderPlus className="w-3.5 h-3.5" />}
          {status.folders_created ? "Ricrea cartelle" : "Crea cartelle"}
        </button>
        <button onClick={syncFiles} disabled={isSync || status.pending_files === 0} className="btn-primary text-xs disabled:opacity-50" data-testid="nc-sync">
          {isSync ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Sincronizza {status.pending_files > 0 ? `(${status.pending_files})` : "file"}
        </button>
        {status.auto_upload && (<span className="badge badge-completato self-center text-[10px]">Auto-upload attivo</span>)}
        {isWorking && (
          <span className="self-center text-xs" style={{ color: "var(--text-muted)" }}>
            ✓ Puoi continuare a lavorare su altre schermate
          </span>
        )}
      </div>
    </div>
  );
}
